import { trackMixpanelEvent } from './initMixpanel';
import { MixpanelFlow, MixpanelEvent, CampaignFlowType } from './mixpanelConstants';
import { CampaignChannels } from 'domains/campaigns/types';

/**
 * Track a Cured-specific Mixpanel event with automatic prefix and optional flow categorization
 *
 * This utility ensures all Cured events are consistently prefixed with "Cured:"
 * and categorized by flow (e.g., Campaign, Insights, Content, etc.)
 *
 * All events MUST be defined in the MixpanelEvent enum to ensure consistency.
 * If you need a new event, add it to mixpanelConstants.ts
 *
 * @param eventName - The event name (must use MixpanelEvent enum)
 * @param properties - Optional event properties
 * @param flow - Optional flow/feature area (must use MixpanelFlow enum)
 *
 */
export const trackCuredEvent = (eventName: MixpanelEvent, properties?: Record<string, any>, flow?: MixpanelFlow) => {
    const prefixedEventName = flow ? `Cured: ${flow} - ${eventName}` : `Cured - ${eventName}`;

    // Automatically add category property for all events
    const enrichedProperties = {
        category: 'Cured',
        ...properties,
    };

    trackMixpanelEvent(prefixedEventName, enrichedProperties);
};

/**
 * Helper function to create an onAbandon handler for campaign flows.
 *
 * @param flowType - The type of campaign flow from CampaignFlowType enum
 * @param campaignType - The type of campaign (defaults to CampaignChannels.EMAIL)
 * @returns A callback function for the onAbandon prop
 *
 * @example
 * <CampaignFlowRouteLeavingGuard
 *   onAbandon={createCampaignAbandonHandler(CampaignFlowType.SINGLE_SEND)}
 * />
 */
export const createCampaignAbandonHandler = (
    flowType: CampaignFlowType,
    campaignType: CampaignChannels | string = CampaignChannels.EMAIL
) => (destination: string) =>
    trackCuredEvent(
        MixpanelEvent.CAMPAIGN_ABANDONED,
        {
            campaign_type: campaignType,
            flow_type: flowType,
            destination,
        },
        MixpanelFlow.CAMPAIGN
    );

/**
 * Helper function to track campaign published event.
 *
 * @param flowType - The type of campaign flow from CampaignFlowType enum
 * @param campaignName - The name of the campaign
 * @param additionalProperties - Optional additional properties (e.g., number_of_sends)
 * @param campaignType - The type of campaign channel (defaults to CampaignChannels.EMAIL)
 *
 * @example
 * trackCampaignPublished(CampaignFlowType.SINGLE_SEND, 'My Campaign');
 * trackCampaignPublished(CampaignFlowType.EMAIL_SERIES, 'My Series', { number_of_sends: 3 });
 */
export const trackCampaignPublished = (
    flowType: CampaignFlowType,
    campaignName: string,
    additionalProperties?: Record<string, any>,
    campaignType: CampaignChannels | string = CampaignChannels.EMAIL
) => {
    trackCuredEvent(
        MixpanelEvent.CAMPAIGN_PUBLISHED,
        {
            campaign_type: campaignType,
            flow_type: flowType,
            campaign_name: campaignName,
            ...additionalProperties,
        },
        MixpanelFlow.CAMPAIGN
    );
};
